import React, { useEffect, useState } from "react";
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from "react-router-dom";
import Properties from "./pages/Properties";
import LandingPage from "./pages/LandingPage";
import PropertyDetails from "./pages/PropertyDetails";
import SelectRole from "./pages/SelectRole";
import Header from "./components/Header";
import FaqPage from "./pages/FaqPage";
import Footer from "./components/Footer";
import ContactoPage from "./pages/ContactoPage";
import AvisoLegalPage from "./pages/AvisoLegalPage";
import ApiDocsPage from "./pages/ApiDocsPage";
import SolicitarApiKeyPage from "./pages/SolicitarApyKeyPage";
import RequireRole from "./components/RequireRol";
import AdminDashboard from "./pages/AdminDashboard";
import NewProperty from "./pages/NewProperty";
import PropertyImageUpload from "./components/PropertyImageUpload";
import MyProperties from "./pages/MyProperties";
import EditProperty from "./pages/EditProperty";
import PropertyAnalysis from "./pages/PropertyAnalysis";
import ConstructorListPage from './pages/ConstructorListPage';
import ConstructorFormPage from './pages/ConstructorFormPage';
import ConstructorDetailPage from './pages/ConstructorDetailPage';
import ActuacionesListPage from './pages/ActuacionesListPage';
import ColeccionClonePage from './pages/ColeccionClonePage';
import ColeccionEditarPage from './pages/ColeccionEditarPage';
import AnalyzeProperty from './pages/AnalyzeProperty';
import AnalyzePropertyImport from './pages/AnalyzePropertyImport';
import AnalyzePropertyImages from './pages/AnalyzePropertyImages';
import AnalyzePropertyData from './pages/AnalyzePropertyData';
import AnalyzePropertyStep1 from './pages/AnalyzePropertyStep1';
import AnalyzePropertyReport from './pages/AnalyzePropertyReport';
import PropertyReport from './pages/PropertyReport';
import MisInformes from './pages/MisInformes';
import VerifyEmailPage from './pages/VerifyEmailPage';
import LoginPage from './pages/LoginPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import LogoutPage from './pages/LogoutPage';
import AgentChat from './components/AgentChat';
import AjustesPage from './pages/AjustesPage';
import AdminAgentStatsPage from './pages/AdminAgentStatsPage';
import LandingHome from './pages/LandingHome';
import LandingInversor from './pages/LandingInversor';
import LandingAgenteInmobiliario from './pages/LandingAgenteInmobiliario';
import LandingConstructor from './pages/LandingConstructor';
import LandingComprador from './pages/LandingComprador';
import LandingPropietario from './pages/LandingPropietario';
import LandingDecorador from './pages/LandingDecorador';
import './App.css';
import { Toaster } from 'react-hot-toast';

const App = () => {
    const [usuario, setUsuario] = useState(null);
    const [roles, setRoles] = useState([]);
    const [originalPath, setOriginalPath] = useState(null);

    const handleLogout = () => {
        fetch(`${window.REACT_APP_API_ENDPOINT}/auth/logout`, {
            credentials: "include",
        }).then(() => {
            setUsuario(null);
            window.location.href = "/logout";
        });
    };

    useEffect(() => {
        fetch(`${window.REACT_APP_API_ENDPOINT}/api/users/current-user`, { credentials: "include" })
            .then((res) => res.json())
            .then((data) => setUsuario(data.user))
            .catch(() => setUsuario(null));

        fetch(`${window.REACT_APP_API_ENDPOINT}/api/roles`)
            .then((res) => res.json())
            .then((data) => setRoles(data.roles))
            .catch(() => setRoles([]));
    }, []);

    const location = useLocation();

    const isLanding = location.pathname === "/" || location.pathname.startsWith("/landing");
    const isAnalyzePath = location.pathname.startsWith("/analizarpropiedad");
    const isLandingPerfil = location.pathname === "/" || location.pathname.startsWith("/landing");

    // Si usuario está logado pero no tiene rol, redirige a selección de rol
    if (usuario && usuario.rol_id == null && location.pathname !== "/select-role" && !isLanding && !isAnalyzePath) {
        return <Navigate to="/select-role" replace />;
    }

    return (
        <div className={isLanding ? "min-h-screen" : "bg-gray-100 min-h-screen p-4"}>
            <Toaster position="top-right" />
            {!isLandingPerfil && <Header usuario={usuario} onLogout={handleLogout} />}
            {!isLanding && <div className="mb-6" />}
            <Routes>
                <Route path="/" element={<LandingHome />} />
                <Route path="/landing" element={<Navigate to="/" replace />} />
                <Route path="/landing/inversor" element={<LandingInversor />} />
                <Route path="/landing/agente-inmobiliario" element={<LandingAgenteInmobiliario />} />
                <Route path="/landing/constructor" element={<LandingConstructor />} />
                <Route path="/landing/comprador" element={<LandingComprador />} />
                <Route path="/landing/propietario" element={<LandingPropietario />} />
                <Route path="/landing/decorador" element={<LandingDecorador />} />
                <Route path="/listado" element={<Properties />} />
                <Route path="/propiedad/:id" element={<PropertyDetails />} />
                <Route
                    path="/select-role"
                    element={
                        <SelectRole
                            usuario={usuario}
                            setUsuario={setUsuario}
                            roles={roles}
                            originalPath={originalPath}
                        />
                    }
                />
                <Route path="/faq" element={<FaqPage />} />
                <Route path="/contacto" element={<ContactoPage />} />
                <Route path="/aviso-legal" element={<AvisoLegalPage />} />
                <Route path="/api-docs" element={<ApiDocsPage />} />
                <Route path="/solicitar-api-key" element={<SolicitarApiKeyPage />} />
                <Route path="/admin" element={<RequireRole allowedRoles={['admin']}><AdminDashboard /></RequireRole>} />
                <Route path="/admin/agent-stats" element={<RequireRole allowedRoles={['admin']}><AdminAgentStatsPage /></RequireRole>} />
                <Route path="/propiedades/nueva" element={<RequireRole allowedRoles={['propietario', 'admin', 'gestor_inmobiliario']}><NewProperty /></RequireRole>} />
                <Route 
                    path="/propiedades/:id/imagenes" 
                    element={
                        <RequireRole allowedRoles={['propietario', 'admin', 'gestor_inmobiliario']}>
                            <PropertyImageUpload />
                        </RequireRole>
                    } 
                />
                <Route 
                    path="/mis-propiedades" 
                    element={
                        <RequireRole allowedRoles={['propietario', 'admin', 'gestor_inmobiliario']}>
                            <MyProperties />
                        </RequireRole>
                    } 
                />
                <Route 
                    path="/propiedades/:id/editar" 
                    element={
                        <RequireRole allowedRoles={['propietario', 'admin', 'gestor_inmobiliario']}>
                            <EditProperty />
                        </RequireRole>
                    } 
                />
                <Route 
                    path="/propiedades/:id/editar/imagenes" 
                    element={
                        <RequireRole allowedRoles={['propietario', 'admin', 'gestor_inmobiliario']}>
                            <PropertyImageUpload />
                        </RequireRole>
                    } 
                />
                <Route path="/propiedades/:id/analizar" element={<RequireRole allowedRoles={['propietario', 'admin', 'gestor_inmobiliario']}><PropertyAnalysis /></RequireRole>} />
                
                {/* Constructor Routes */}
                <Route 
                    path="/constructores"
                    element={
                        <RequireRole allowedRoles={['admin', 'gestor_inmobiliario']}>
                            <ConstructorListPage />
                        </RequireRole>
                    }
                />
                <Route 
                    path="/constructores/nuevo"
                    element={
                        <RequireRole allowedRoles={['admin', 'gestor_inmobiliario']}>
                            <ConstructorFormPage />
                        </RequireRole>
                    }
                />
                <Route 
                    path="/constructores/editar/:id"
                    element={
                        <RequireRole allowedRoles={['admin', 'gestor_inmobiliario']}>
                            <ConstructorFormPage />
                        </RequireRole>
                    }
                />
                <Route 
                    path="/constructores/:id"
                    element={
                        <RequireRole allowedRoles={['admin', 'gestor_inmobiliario']}>
                            <ConstructorDetailPage />
                        </RequireRole>
                    }
                />
                {/* Actuaciones Routes */}
                <Route 
                    path="/actuaciones"
                    element={
                        <RequireRole allowedRoles={['admin','gestor_inmobiliario','propietario']}>
                            <ActuacionesListPage user={usuario} />
                        </RequireRole>
                    }
                />
                <Route
                    path="/colecciones/clonar/:id"
                    element={
                        <RequireRole allowedRoles={['admin','gestor_inmobiliario']}>
                            <ColeccionClonePage />
                        </RequireRole>
                    }
                />
                <Route
                    path="/colecciones/editar/:id"
                    element={
                        <RequireRole allowedRoles={['admin','gestor_inmobiliario']}>
                            <ColeccionEditarPage />
                        </RequireRole>
                    }
                />

                {/* Auth pages (no auth required) */}
                <Route path="/login" element={<LoginPage />} />
                <Route path="/logout" element={<LogoutPage />} />
                <Route path="/verificar-email" element={<VerifyEmailPage />} />
                <Route path="/restablecer-password" element={<ResetPasswordPage />} />

                {/* Analyze Property Flow (no auth required) */}
                <Route path="/mis-informes" element={<MisInformes />} />
                <Route path="/analizarpropiedad/nueva" element={<AnalyzeProperty />} />
                <Route path="/analizarpropiedad/:id/importar" element={<AnalyzePropertyImport />} />
                <Route path="/analizarpropiedad/:id/paso1" element={<AnalyzePropertyStep1 />} />
                <Route path="/analizarpropiedad/:id/imagenes" element={<AnalyzePropertyImages />} />
                <Route path="/analizarpropiedad/:id/datos" element={<AnalyzePropertyData />} />
                <Route path="/analizarpropiedad/:id/informe" element={<AnalyzePropertyReport />} />
                <Route path="/analizarpropiedad/:id/informe-detalle" element={<PropertyReport />} />
                <Route
                    path="/ajustes"
                    element={
                        <RequireRole allowedRoles={['admin','inversor','propietario','gestor_inmobiliario','constructor','perito','interiorista']}>
                            <AjustesPage />
                        </RequireRole>
                    }
                />
            </Routes>
            {!isLandingPerfil && <Footer />}
            {usuario && !isLandingPerfil && <AgentChat usuario={usuario} />}
        </div>
    );
};

const WrappedApp = () => (
    <Router>
        <App />
    </Router>
);

export default WrappedApp;
